<div class="p-6">
    <h2 class="text-2xl font-bold mb-6">Narzędzia KSeF</h2>
    
    <!-- Sekcja: Środowisko testowe -->
    <mat-card class="mb-6">
        <mat-card-header>
            <mat-icon mat-card-avatar class="text-orange-500" svgIcon="tabler_outline:test-pipe"></mat-icon>
            <mat-card-title>Środowisko testowe</mat-card-title>
            <mat-card-subtitle>Zarządzanie danymi z testowego środowiska KSeF</mat-card-subtitle>
        </mat-card-header>
        
        <mat-card-content class="mt-4">
            <p class="text-gray-600 dark:text-gray-400 mb-4">
                Ta operacja usunie wszystkie dane związane z testowym środowiskiem KSeF:
            </p>
            
            <ul class="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-1">
                <li>Faktury KSeF zsynchronizowane z środowiska testowego</li>
                <li>Sesje batch oznaczone jako testowe</li>
                <li>Pola KSeF z dokumentów wysłanych do środowiska testowego</li>
                <li>Logi aktywności związane z testowym środowiskiem</li>
            </ul>
            
            <div class="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 mb-6">
                <div class="flex items-start gap-3">
                    <mat-icon class="text-orange-600 dark:text-orange-400 flex-shrink-0" svgIcon="tabler_outline:alert-triangle"></mat-icon>
                    <div>
                        <p class="font-semibold text-orange-800 dark:text-orange-200">Uwaga!</p>
                        <p class="text-sm text-orange-700 dark:text-orange-300">
                            Ta operacja jest nieodwracalna. Upewnij się, że chcesz usunąć wszystkie dane testowe.
                            Operacja nie wpływa na dane z produkcyjnego środowiska KSeF.
                        </p>
                    </div>
                </div>
            </div>
        </mat-card-content>
        
        <mat-card-actions class="px-4 pb-4">
            <button 
                mat-raised-button 
                color="warn"
                (click)="confirmClearTestData()"
                [disabled]="isClearing">
                <mat-icon *ngIf="!isClearing" svgIcon="tabler_outline:trash"></mat-icon>
                <mat-spinner *ngIf="isClearing" diameter="20" class="mr-2"></mat-spinner>
                <span class="ml-2">{{ isClearing ? 'Usuwanie...' : 'Usuń dane z środowiska testowego' }}</span>
            </button>
        </mat-card-actions>
    </mat-card>
    
    <!-- Wyniki ostatniej operacji -->
    <mat-card *ngIf="lastResult" class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
        <mat-card-header>
            <mat-icon mat-card-avatar class="text-green-500" svgIcon="tabler_outline:check"></mat-icon>
            <mat-card-title class="text-green-800 dark:text-green-200">Operacja zakończona pomyślnie</mat-card-title>
        </mat-card-header>
        
        <mat-card-content class="mt-4">
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div class="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                    <div class="text-2xl font-bold text-gray-800 dark:text-gray-200">{{ lastResult.ksefInvoicesDeleted }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">Faktur KSeF</div>
                </div>
                <div class="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                    <div class="text-2xl font-bold text-gray-800 dark:text-gray-200">{{ lastResult.batchSessionsDeleted }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">Sesji batch</div>
                </div>
                <div class="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                    <div class="text-2xl font-bold text-gray-800 dark:text-gray-200">{{ lastResult.documentsCleared }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">Dokumentów</div>
                </div>
                <div class="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                    <div class="text-2xl font-bold text-gray-800 dark:text-gray-200">{{ lastResult.activityLogsMarkedDeleted }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">Logów</div>
                </div>
            </div>
        </mat-card-content>
    </mat-card>

    <!-- Sekcja: Unified Document Store Migration -->
    <mat-card class="mb-6 mt-6">
        <mat-card-header>
            <mat-icon mat-card-avatar class="text-blue-500" svgIcon="tabler_outline:database-import"></mat-icon>
            <mat-card-title>Migracja Unified Document Store</mat-card-title>
            <mat-card-subtitle>Aktualizacja pól dokumentów (lazy update)</mat-card-subtitle>
        </mat-card-header>
        
        <mat-card-content class="mt-4">
            <p class="text-gray-600 dark:text-gray-400 mb-4">
                Aktualizuje dokumenty, które nie mają jeszcze nowych pól Unified Document Store:
            </p>
            
            <ul class="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-1">
                <li><strong>source</strong> → 'local' (domyślnie)</li>
                <li><strong>direction</strong> → 'income' lub 'expense' (na podstawie isCost)</li>
                <li><strong>isKsefRequired</strong> → false dla: Proforma, WZ, PZ, MM, Zamówienie, Oferta</li>
                <li><strong>isKsefSynced</strong> → true jeśli dokument ma ksefNumber lub ksefReferenceNumber</li>
                <li><strong>linkedDocuments</strong> → pusta tablica</li>
            </ul>
            
            <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                <div class="flex items-start gap-3">
                    <mat-icon class="text-blue-600 dark:text-blue-400 flex-shrink-0" svgIcon="tabler_outline:info-circle"></mat-icon>
                    <div>
                        <p class="font-semibold text-blue-800 dark:text-blue-200">Informacja</p>
                        <p class="text-sm text-blue-700 dark:text-blue-300">
                            Operacja jest bezpieczna i idempotentna – można ją uruchomić wielokrotnie.
                            Jeśli wszystkie dokumenty mają już nowe pola, operacja zakończy się natychmiast.
                        </p>
                    </div>
                </div>
            </div>
        </mat-card-content>
        
        <mat-card-actions class="px-4 pb-4">
            <button 
                mat-raised-button 
                color="primary"
                (click)="confirmRunLazyUpdate()"
                [disabled]="isRunningLazyUpdate">
                <mat-icon *ngIf="!isRunningLazyUpdate" svgIcon="tabler_outline:database-import"></mat-icon>
                <mat-spinner *ngIf="isRunningLazyUpdate" diameter="20" class="mr-2"></mat-spinner>
                <span class="ml-2">{{ isRunningLazyUpdate ? 'Aktualizowanie...' : 'Uruchom migrację dokumentów' }}</span>
            </button>
        </mat-card-actions>
    </mat-card>

    <!-- Wyniki migracji -->
    <mat-card *ngIf="lazyUpdateResult" class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
        <mat-card-header>
            <mat-icon mat-card-avatar class="text-green-500" svgIcon="tabler_outline:check"></mat-icon>
            <mat-card-title class="text-green-800 dark:text-green-200">Migracja zakończona</mat-card-title>
            <mat-card-subtitle *ngIf="lazyUpdateResult.durationMs" class="text-green-600 dark:text-green-400">
                Czas wykonania: {{ lazyUpdateResult.durationMs }}ms
            </mat-card-subtitle>
        </mat-card-header>
        
        <mat-card-content class="mt-4">
            <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div class="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                    <div class="text-2xl font-bold text-gray-800 dark:text-gray-200">{{ lazyUpdateResult.documentsToUpdate }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">Do aktualizacji</div>
                </div>
                <div class="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                    <div class="text-2xl font-bold text-gray-800 dark:text-gray-200">{{ lazyUpdateResult.step1_defaults }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">Wartości domyślne</div>
                </div>
                <div class="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                    <div class="text-2xl font-bold text-gray-800 dark:text-gray-200">{{ lazyUpdateResult.step2_expenses }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">Expense</div>
                </div>
                <div class="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                    <div class="text-2xl font-bold text-gray-800 dark:text-gray-200">{{ lazyUpdateResult.step3_ksefSynced }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">KSeF synced</div>
                </div>
                <div class="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                    <div class="text-2xl font-bold text-gray-800 dark:text-gray-200">{{ lazyUpdateResult.step4_nonKsef }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">Bez KSeF</div>
                </div>
            </div>
        </mat-card-content>
    </mat-card>

    <!-- ============================================ -->
    <!-- Sekcja: Wyślij testową fakturę do KSeF -->
    <!-- ============================================ -->
    <mat-card class="mb-6 mt-6">
        <mat-card-header>
            <mat-icon mat-card-avatar class="text-purple-500" svgIcon="tabler_outline:send"></mat-icon>
            <mat-card-title>Wyślij testową fakturę do KSeF</mat-card-title>
            <mat-card-subtitle>Diagnostyka problemów z wysyłką faktur użytkowników</mat-card-subtitle>
        </mat-card-header>
        
        <mat-card-content class="mt-4">
            <p class="text-gray-600 dark:text-gray-400 mb-4">
                Wklej JSON dokumentu z bazy danych (db document usera), aby przetestować wysyłkę do KSeF
                przez Twoje połączenie. Żadne dane w bazie nie zostaną zmienione.
            </p>

            <!-- Blokada produkcyjna -->
            <div *ngIf="isEnvironmentLoading" class="flex items-center gap-2 mb-4">
                <mat-spinner diameter="20"></mat-spinner>
                <span class="text-gray-500 dark:text-gray-400 text-sm">Sprawdzam środowisko...</span>
            </div>

            <div *ngIf="!isEnvironmentLoading && isTestSendBlocked" 
                class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                <div class="flex items-start gap-3">
                    <mat-icon class="text-red-600 dark:text-red-400 flex-shrink-0" svgIcon="tabler_outline:lock"></mat-icon>
                    <div>
                        <p class="font-semibold text-red-800 dark:text-red-200">Zablokowane na produkcji</p>
                        <p class="text-sm text-red-700 dark:text-red-300">
                            {{ blockReason || 'Testowa wysyłka faktur jest zablokowana na środowisku produkcyjnym. Wysłanie faktury do KSeF na produkcji miałoby skutki podatkowe.' }}
                        </p>
                    </div>
                </div>
            </div>

            <div *ngIf="!isEnvironmentLoading && !isTestSendBlocked"
                class="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-6">
                <div class="flex items-start gap-3">
                    <mat-icon class="text-purple-600 dark:text-purple-400 flex-shrink-0" svgIcon="tabler_outline:info-circle"></mat-icon>
                    <div>
                        <p class="font-semibold text-purple-800 dark:text-purple-200">Środowisko: {{ ksefEnvironment }}</p>
                        <p class="text-sm text-purple-700 dark:text-purple-300">
                            Faktura zostanie wysłana do środowiska testowego KSeF przez Twoje połączenie.
                            Operacja nie modyfikuje żadnych danych w bazie.
                        </p>
                    </div>
                </div>
            </div>

            <!-- Pole JSON -->
            <mat-form-field appearance="outline" class="w-full">
                <mat-label>JSON dokumentu (db document)</mat-label>
                <textarea matInput
                    [(ngModel)]="testInvoiceJson"
                    rows="12"
                    placeholder='{"documentType": "Faktura", "documentNumber": "FV/001/2025", ...}'
                    [disabled]="isTestSendBlocked || isSendingTestInvoice"
                    class="font-mono text-sm"></textarea>
                <mat-hint>Wklej pełny JSON dokumentu z MongoDB</mat-hint>
            </mat-form-field>
        </mat-card-content>
        
        <mat-card-actions class="px-4 pb-4">
            <button 
                mat-raised-button 
                color="primary"
                (click)="confirmSendTestInvoice()"
                [disabled]="isTestSendBlocked || isSendingTestInvoice || !testInvoiceJson.trim()">
                <mat-icon *ngIf="!isSendingTestInvoice" svgIcon="tabler_outline:send"></mat-icon>
                <mat-spinner *ngIf="isSendingTestInvoice" diameter="20" class="mr-2"></mat-spinner>
                <span class="ml-2">{{ isSendingTestInvoice ? 'Wysyłanie do KSeF...' : 'Wyślij testowo do KSeF' }}</span>
            </button>
        </mat-card-actions>
    </mat-card>

    <!-- Wynik wysłania testowej faktury - Błąd -->
    <mat-card *ngIf="testInvoiceError" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 mb-6">
        <mat-card-header>
            <mat-icon mat-card-avatar class="text-red-500" svgIcon="tabler_outline:x"></mat-icon>
            <mat-card-title class="text-red-800 dark:text-red-200">Błąd wysyłki</mat-card-title>
        </mat-card-header>
        <mat-card-content class="mt-4">
            <pre class="text-sm text-red-700 dark:text-red-300 whitespace-pre-wrap break-words bg-red-100 dark:bg-red-900/30 p-4 rounded-lg">{{ testInvoiceError }}</pre>
        </mat-card-content>
    </mat-card>

    <!-- Wynik wysłania testowej faktury - Sukces / Odrzucona -->
    <mat-card *ngIf="testInvoiceResult" 
        [ngClass]="{
            'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800': testInvoiceResult.success,
            'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800': !testInvoiceResult.success
        }"
        class="mb-6">
        <mat-card-header>
            <mat-icon mat-card-avatar 
                [ngClass]="{'text-green-500': testInvoiceResult.success, 'text-yellow-500': !testInvoiceResult.success}"
                [svgIcon]="testInvoiceResult.success ? 'tabler_outline:check' : 'tabler_outline:alert-triangle'"></mat-icon>
            <mat-card-title [ngClass]="{'text-green-800 dark:text-green-200': testInvoiceResult.success, 'text-yellow-800 dark:text-yellow-200': !testInvoiceResult.success}">
                {{ testInvoiceResult.success ? 'Faktura wysłana pomyślnie' : 'Faktura odrzucona przez KSeF' }}
            </mat-card-title>
            <mat-card-subtitle>Środowisko: {{ testInvoiceResult.environment }}</mat-card-subtitle>
        </mat-card-header>
        
        <mat-card-content class="mt-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div *ngIf="testInvoiceResult.ksefNumber" class="p-3 bg-white dark:bg-gray-800 rounded-lg">
                    <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">Numer KSeF</div>
                    <div class="text-sm font-mono font-bold text-gray-800 dark:text-gray-200">{{ testInvoiceResult.ksefNumber }}</div>
                </div>
                <div *ngIf="testInvoiceResult.ksefStatusCode" class="p-3 bg-white dark:bg-gray-800 rounded-lg">
                    <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">Kod statusu KSeF</div>
                    <div class="text-sm font-bold text-gray-800 dark:text-gray-200">{{ testInvoiceResult.ksefStatusCode }}</div>
                </div>
                <div *ngIf="testInvoiceResult.sessionReferenceNumber" class="p-3 bg-white dark:bg-gray-800 rounded-lg">
                    <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">Sesja ref.</div>
                    <div class="text-sm font-mono text-gray-800 dark:text-gray-200">{{ testInvoiceResult.sessionReferenceNumber }}</div>
                </div>
                <div *ngIf="testInvoiceResult.invoiceReferenceNumber" class="p-3 bg-white dark:bg-gray-800 rounded-lg">
                    <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">Faktura ref.</div>
                    <div class="text-sm font-mono text-gray-800 dark:text-gray-200">{{ testInvoiceResult.invoiceReferenceNumber }}</div>
                </div>
            </div>

            <!-- Status KSeF szczegóły (dla odrzuconych faktur) -->
            <div *ngIf="testInvoiceResult.ksefStatusDescription" class="mb-4">
                <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">Opis statusu KSeF</div>
                <pre class="text-sm whitespace-pre-wrap break-words bg-white dark:bg-gray-800 p-3 rounded-lg">{{ testInvoiceResult.ksefStatusDescription }}</pre>
            </div>

            <div *ngIf="testInvoiceResult.ksefStatusDetails?.length" class="mb-4">
                <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">Szczegóły błędów</div>
                <ul class="list-disc list-inside text-sm bg-white dark:bg-gray-800 p-3 rounded-lg">
                    <li *ngFor="let detail of testInvoiceResult.ksefStatusDetails">{{ detail }}</li>
                </ul>
            </div>

            <!-- Ostrzeżenia mapowania -->
            <div *ngIf="testInvoiceResult.warnings?.length" class="mb-4">
                <div class="text-xs text-yellow-600 dark:text-yellow-400 mb-1">Ostrzeżenia mapowania</div>
                <ul class="list-disc list-inside text-sm text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
                    <li *ngFor="let warning of testInvoiceResult.warnings">{{ warning.field }}: {{ warning.message }}</li>
                </ul>
            </div>

            <!-- Wygenerowany XML (collapsible) -->
            <details *ngIf="testInvoiceResult.generatedXml" class="mt-4">
                <summary class="cursor-pointer text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                    Wygenerowany XML FA(3) (kliknij aby rozwinąć)
                </summary>
                <pre class="mt-2 text-xs font-mono whitespace-pre-wrap break-words bg-gray-100 dark:bg-gray-800 p-4 rounded-lg max-h-96 overflow-auto">{{ testInvoiceResult.generatedXml }}</pre>
            </details>
        </mat-card-content>
    </mat-card>
</div>
